import { FC, useEffect, useState } from "react";
import { CreatePlacementRequest, PlacementDoc, PlacementRequestReceiver } from "../../../models/placements";
import { Tender } from "../../../models/tendering";
import { Filter } from "../../../models/filters";
import { Dialog, DialogSurface, Title2, Title3, Text, DialogContent, Subtitle1, DialogActions, Button, Radio, RadioGroup, Field, Input, Textarea, Caption1, Accordion, AccordionHeader, AccordionPanel, AccordionItem, Spinner } from "@fluentui/react-components";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { createPlacementRequest, updatePlacement } from "../../../services/placementService";
import { on } from "events";
import ErrorDialog from "../../dialogs/ErrorDialog";

interface SendRequestProps{
    isOpen:boolean;
    onClose: () => void;
    tenderList: Tender[];
    placement:PlacementDoc;
    placementDocID: string;
    requestSent: (doc: PlacementDoc) => void;
    tenderingName?: string;
}

export const SendRequest: FC<SendRequestProps> = ({
  isOpen,
  onClose,
  tenderList,
  placement,
  placementDocID,
  requestSent,
  tenderingName
}) => {
  const [answerDate, setAnswerDate] = useState<Date | null>(placement.answerDate ? new Date(placement.answerDate) : null);
  const [descriptionText, setDescriptionText] = useState(placement.descriptionText);
  const [backgroundText, setBackgroundText] = useState(placement.backgroundText);
  const [isLoading, setIsLoading] = useState(false);
  const [isConflictDialogOpen, setIsConflictDialogOpen] = useState(false);

  const getFilterValue = (filter: Filter): string => {
    return filter.type === 'bool' ? (filter.value == 1 ? 'Ja' : 'Nej') : filter.value;
  };

  const sendPlacementRequest = async () => {
    setIsLoading(true);
    try {
      let pReceivers: PlacementRequestReceiver[] = [];
      tenderList.forEach(t => {
        if (!t.denied) {
          pReceivers.push({
            placementRequestReceiverFirstName: t.tenderName,
            placementRequestReceiverLastName: '',
            placementRequestReceiverEmail: t.contact?.email?.toString() ?? "",
            placementRequestReceiverPhoneNumber: t.contact?.phone?.toString() ?? "",
            placementRequestReceiverReference: t.tenderID.toString(),
            isPlacementRequestAccepted: false
          });
        }
      });
      const pRequest: CreatePlacementRequest = {
        placementRequestDocumentID: placementDocID,
        placementRequestTokenValidUntil: answerDate?.toISOString() || placement.answerDate,
        placementRequestNotificationProcessDate: placement.plannedDate,
        placementRequestReceivers: pReceivers
      };

      await createPlacementRequest(pRequest);
      
      const updatedPlacement = {
        ...placement,
        descriptionText,
        backgroundText,
        logs: {
          ...placement.logs,
          requestSent: new Date().toISOString()
        },
        status: 'sent',
        answerDate: answerDate?.toISOString() || placement.answerDate
      };
      
      await updatePlacement(updatedPlacement, placementDocID);
      requestSent(updatedPlacement);
    }
    catch (error: any) {
      if (error?.response?.status === 409) {
        setIsConflictDialogOpen(true);
      } else {
        console.error("Error sending placement request:", error);
      }
    } finally {
      setIsLoading(false);
      onClose();
    }
  }

  const print = (): void => {
    const printContent = `<div>
        <div style="display: flex; flex-direction: column;">
            <div><h5>Referens: </h5><p>${placement.reference}</p></div>
            <div><h5>Rubrik: </h5><p>${placement.name}</p></div>
            <div><h5>Behov: </h5><p>${descriptionText}</p></div>
            <div><h5>Bakgrund: </h5><p>${backgroundText}</p></div>
            <div><h5>Svar senast: </h5><p>${new Date(placement.answerDate).toLocaleDateString('en-US')}</p></div>
            <div><h5>Planerad start för uppdrag: </h5><p>${new Date(placement.plannedDate).toLocaleDateString('en-US')}</p></div>
        </div>
    </div>`;
  
    const newWindow = window.open('', '', 'height=600,width=800');
    if (!newWindow) return;
    
    newWindow.document.write('<html><head><title>Förfrågan</title>');
    newWindow.document.write('<style>body { font-family: Arial, sans-serif; } div { display: block; margin-bottom: 10px; } h5, p { display: inline; margin: 0; }</style>');
    newWindow.document.write('</head><body>');
    newWindow.document.write(printContent);
    newWindow.document.write('</body></html>');
    newWindow.document.close();
    newWindow.focus();
    newWindow.print();
    newWindow.close();
  }

  return (
    <><Dialog open={isOpen} onOpenChange={(event, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogContent className="flex flex-col gap-2">
          <Title3>{placement.status === 'sent' ? "Skickad förfrågan" : "Skicka förfrågan"}</Title3>
          {placement.status === 'sent' && (
            <div className="flex flex-col gap-1">
              <div className="flex gap-2">
                <Text weight="semibold">Placering:</Text>
                <Text>{placement.name}</Text>
              </div>
              {tenderingName && (
                <div className="flex gap-2">
                  <Text weight="semibold">Ramavtal:</Text>
                  <Text>{tenderingName}</Text>
                </div>
              )}
              <div className="flex gap-2">
                <Text weight="semibold">Förfrågan skickad:</Text>
                <Text>{placement.logs?.requestSent ? new Date(placement.logs.requestSent).toLocaleString('sv-SE') : '–'}</Text>
              </div>
              <div className="flex gap-2">
                <Text weight="semibold">Återstående:</Text>
                <Text>{tenderList.filter(t => {
                  if (t.denied) return false;
                  const answerDatePassed = placement.answerDate && new Date(placement.answerDate) < new Date();
                  if (answerDatePassed && t.placementRequestReceiverStatus === 'Rejected') return false;
                  return true;
                }).length} verksamheter</Text>
              </div>
              <div className="flex gap-2">
                <Text weight="semibold">Bortvalda:</Text>
                <Text>{tenderList.filter(t => t.denied).length} verksamheter</Text>
              </div>
              <div className="flex gap-2">
                <Text weight="semibold">Skickades till:</Text>
                <Text>{tenderList.filter(t => t.placementRequestReceiverID != null).length} verksamheter</Text>
              </div>
            </div>
          )}
          {tenderList && placement.status !== 'sent' && <Text>Kommer skicka förfrågan till {tenderList.filter(t => !t.denied).length} verksamheter</Text>}
          <Accordion collapsible>
            <AccordionItem value="placementDetails">
              <AccordionHeader>
                <Text>Innehåll i utskick</Text>
              </AccordionHeader>
              <AccordionPanel className="flex flex-col gap-2">
                {placement.status !== 'sent' ? (
                  <>
                    <Field label="Behov" required>
                      <Textarea
                        value={descriptionText}
                        onChange={(e, data) => setDescriptionText(data.value)}
                        rows={4}
                        disabled={isLoading} />
                    </Field>
                    <Field label="Bakgrund" required>
                      <Textarea
                        value={backgroundText}
                        onChange={(e, data) => setBackgroundText(data.value)}
                        rows={4}
                        disabled={isLoading} />
                    </Field>
                  </>
                ) : (
                  <>
                    <Text weight="semibold">Behov:</Text>
                    <Text>{placement.descriptionText}</Text>
                    <Text weight="semibold">Bakgrund:</Text>
                    <Text>{placement.backgroundText}</Text>
                  </>
                )}
              </AccordionPanel>
            </AccordionItem>
            {placement.activeFilters && ((placement.activeFilters.baseFilters?.length > 0) || (placement.activeFilters.filters?.length > 0)) && (
              <AccordionItem value="activeFilters">
                <AccordionHeader>
                  <Text>Valda filter ({(placement.activeFilters.baseFilters?.length || 0) + (placement.activeFilters.filters?.length || 0)})</Text>
                </AccordionHeader>
                <AccordionPanel>
                  <div className="flex flex-col gap-1 mt-1">
                    {placement.activeFilters.baseFilters?.map((filter: Filter, index: number) => (
                      <div key={index} className="flex gap-2">
                        <Text weight="semibold">{filter.text}:</Text>
                        <Text>{getFilterValue(filter)}</Text>
                      </div>
                    ))}
                    {placement.activeFilters.filters?.map((filter: Filter, index: number) => (
                      <div key={index} className="flex gap-2">
                        <Text weight="semibold">{filter.text}:</Text>
                        <Text>{getFilterValue(filter)}</Text>
                      </div>
                    ))}
                  </div>
                </AccordionPanel>
              </AccordionItem>
            )}
          </Accordion>
          {placement.status === 'sent' && <Text>Svar senast: {placement.answerDate ? new Date(placement.answerDate).toLocaleDateString('sv-SE') : ''}</Text>}
          {placement.status !== 'sent' && <Field label={"Svar senast:"}>
            <DatePicker
              value={answerDate}
              formatDate={(date?: Date) => { if (!date) return ''; return date.toLocaleDateString('sv-SE'); } }
              onSelectDate={(date: Date | null | undefined) => {
                if (date) {
                  date.setHours(23, 59, 59, 999);
                }
                setAnswerDate(date ?? null);
              } }
              showWeekNumbers={true}
              firstWeekOfYear={1}
              showMonthPickerAsOverlay={true}
              disabled={isLoading} />
          </Field>}
        </DialogContent>
        <DialogActions className="mt-8">
          {placement.status === 'sent' && <Button
            onClick={print}
            disabled={isLoading}
          >
            Skriv ut...
          </Button>}
          {placement.status !== 'sent' && <Button
            onClick={onClose}
            disabled={isLoading}
          >
            Avbryt
          </Button>}
          {placement.status !== 'sent' && <Button
            disabled={!answerDate || !descriptionText || !backgroundText || isLoading}
            appearance="primary"
            onClick={sendPlacementRequest}
            icon={isLoading ? <Spinner size="tiny" /> : undefined}
          >
            {isLoading ? "Skickar..." : "Skicka"}
          </Button>}
          {placement.status === 'sent' && <Button
            disabled={!answerDate || isLoading}
            appearance="primary"
            onClick={onClose}
            icon={isLoading ? <Spinner size="tiny" /> : undefined}
          >
            Stäng
          </Button>}
        </DialogActions>
      </DialogSurface>
      </Dialog>
      <ErrorDialog
        isOpen={isConflictDialogOpen}
        onClose={() => {setIsConflictDialogOpen(false); window.location.reload()}}
        message="Placeringen har redan uppdaterats i en annan flik. Sidan kommer att laddas om." 
      />
      </>
  );
};

export default SendRequest;